import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, Check } from 'lucide-react';

const languages = [
  { id: 'zh-CN', name: '简体中文', flag: '🇨🇳' },
  { id: 'en-US', name: 'English', flag: '🇺🇸' },
];

interface LanguageSwitcherProps {
  compact?: boolean;
}

export function LanguageSwitcher({ compact = false }: LanguageSwitcherProps) {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const currentLanguage = languages.find(lang => lang.id === i18n.language) || languages[0];

  const changeLanguage = (langId: string) => {
    i18n.changeLanguage(langId);
    
    // 同步保存到 userSettings
    const saved = localStorage.getItem('userSettings');
    const settings = saved ? JSON.parse(saved) : {};
    settings.language = langId;
    localStorage.setItem('userSettings', JSON.stringify(settings));
    
    setIsOpen(false);
  };

  if (compact) {
    return (
      <div className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition"
        >
          <Globe className="w-4 h-4" />
          <span>{currentLanguage.flag}</span>
        </button>

        {isOpen && (
          <>
            <div
              className="fixed inset-0 z-10"
              onClick={() => setIsOpen(false)}
            />
            <div className="absolute right-0 mt-2 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1">
              {languages.map((lang) => (
                <button
                  key={lang.id}
                  onClick={() => changeLanguage(lang.id)}
                  className={`w-full flex items-center gap-3 px-4 py-2 text-sm hover:bg-gray-50 transition ${
                    i18n.language === lang.id ? 'text-blue-600' : 'text-gray-700'
                  }`}
                >
                  <span>{lang.flag}</span>
                  <span className="flex-1 text-left">{lang.name}</span>
                  {i18n.language === lang.id && (
                    <Check className="w-4 h-4" />
                  )}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="bg-gray-50 rounded-xl p-6">
      <label className="text-sm font-medium text-gray-700 mb-4 block">
        {i18n.t('settings.language')}
      </label>
      <div className="grid grid-cols-2 gap-3">
        {languages.map((lang) => (
          <button
            key={lang.id}
            onClick={() => changeLanguage(lang.id)}
            className={`px-4 py-3 rounded-lg text-sm transition flex items-center gap-2 ${
              i18n.language === lang.id
                ? 'bg-blue-500 text-white'
                : 'bg-white border border-gray-200 text-gray-700 hover:border-gray-300'
            }`}
          >
            <span>{lang.flag}</span>
            <span>{lang.name}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
